/**
 * IPAMAssignments — /networks/assignments
 * Full assignments table with search by IP/object, filter by type, release.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import IPAMLayout from './IPAMLayout';
import ExportExcelButton from './ExportExcelButton';
import api from '../services/api';
import { assignmentName, ciTypeLabel } from '../utils/ipamCi';

const ASSIGN_TYPE_COLORS = {
  static: 'secondary', carp: 'primary', vip: 'info',
  virtual: 'warning', vrrp: 'success', hsrp: 'danger',
};

export default function IPAMAssignments({ companyId }) {
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const errTimer = useRef(null);

  const [assignments, setAssignments] = useState([]);
  const [ciCatalog, setCiCatalog]     = useState({});

  // Filters
  const [search, setSearch]           = useState('');
  const [filterType, setFilterType]   = useState('');
  const [filterMode, setFilterMode]   = useState('');
  const [sortField, setSortField]     = useState('created_at');
  const [sortDir, setSortDir]         = useState('desc');

  function toggleSort(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }
  function ipNum(ip) {
    return (ip || '').split('.').reduce((acc, o) => acc * 256 + Number(o), 0);
  }
  function SortTh({ field, children, style }) {
    const active = sortField === field;
    return (
      <th style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', ...style }}
          onClick={() => toggleSort(field)}>
        {children} {active ? (sortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.3 }}>⇅</span>}
      </th>
    );
  }

  useEffect(() => {
    if (error) {
      clearTimeout(errTimer.current);
      errTimer.current = setTimeout(() => setError(''), 6000);
    }
    return () => clearTimeout(errTimer.current);
  }, [error]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [a, s, e_, w, p, ph] = await Promise.allSettled([
        api.getIpamAssignments(companyId),
        api.getServers(companyId),
        api.getElements(companyId),
        api.getWorkstations({ company_id: companyId }),
        api.getPrinters({ company_id: companyId }),
        api.getIPPhones(companyId),
      ]);
      const ex = r => r.status === 'fulfilled' ? (r.value?.data || []) : [];
      setAssignments(ex(a));
      const catalog = {};
      const servers = ex(s);
      const elements = ex(e_);
      const ws = ex(w); const printers = ex(p); const phones = ex(ph);
      [...servers.map(x => ({ ...x, _type: 'server' })),
       ...servers.map(x => ({ ...x, _type: 'server_team' })),
       ...elements.map(x => ({ ...x, _type: 'rack_element' })),
       ...ws.map(x => ({ ...x, _type: 'workstation' })),
       ...printers.map(x => ({ ...x, _type: 'printer' })),
       ...phones.map(x => ({ ...x, _type: 'ip_phone' })),
      ].forEach(item => {
        const key = `${item._type}:${item.id}`;
        const n = item.name || item.code || item.hostname || `#${item.id}`;
        catalog[key] = item._type === 'rack_element' ? `[${item.element_type}] ${n}` : n;
      });
      setCiCatalog(catalog);
    } catch (e) {
      setError(e.message || 'Error cargando asignaciones');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const release = async (id) => {
    try {
      await api.deleteIpamAssignment(id, companyId);
      await load();
    } catch (e) { setError(e.message); }
  };

  const allModes   = [...new Set(assignments.map(a => a.assignment_type).filter(Boolean))];
  const allTypes   = [...new Set(assignments.map(a => a.object_type).filter(Boolean))];

  const filtered = assignments.filter(a => {
    if (filterType && a.object_type !== filterType) return false;
    if (filterMode && a.assignment_type !== filterMode) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = (assignmentName(a, ciCatalog) || '').toLowerCase();
      return a.ip.includes(q) || name.includes(q) || (a.interface_ref || '').toLowerCase().includes(q)
        || (a.assigned_by || '').toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => {
    let cmp = 0;
    if (sortField === 'ip')         cmp = ipNum(a.ip) - ipNum(b.ip);
    else if (sortField === 'created_at') cmp = (a.created_at || '').localeCompare(b.created_at || '');
    else if (sortField === 'assigned_by') cmp = (a.assigned_by || '').localeCompare(b.assigned_by || '');
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const exportRows = filtered.map(a => ({
    'IP': a.ip,
    'Objeto': assignmentName(a, ciCatalog),
    'Tipo de objeto': ciTypeLabel(a.object_type),
    'Interfaz': a.interface_ref || '',
    'Modo': (a.assignment_type || 'static').toUpperCase(),
    'Principal': a.is_primary ? 'Sí' : 'No',
    'Fecha asignación': a.created_at ? new Date(a.created_at).toLocaleString() : '',
    'Asignado por': a.assigned_by || ''
  }));

  return (
    <IPAMLayout>
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between">
          <span>{error}</span>
          <button className="btn-close" onClick={() => setError('')} />
        </div>
      )}

      {/* Toolbar */}
      <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: 240 }}
          placeholder="Buscar IP, nombre, interfaz…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="form-select form-select-sm" style={{ maxWidth: 160 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Tipo obj.: todos</option>
          {allTypes.map(t => <option key={t} value={t}>{ciTypeLabel(t)}</option>)}
        </select>
        <select className="form-select form-select-sm" style={{ maxWidth: 120 }} value={filterMode} onChange={e => setFilterMode(e.target.value)}>
          <option value="">Modo: todos</option>
          {allModes.map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
        </select>
        <span className="badge text-bg-secondary ms-auto">{filtered.length} / {assignments.length}</span>
        <ExportExcelButton rows={exportRows} fileBase="asignaciones-ip" sheetName="Asignaciones" className="btn btn-outline-secondary btn-sm" />
        <button className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>↺</button>
      </div>

      {loading && <p className="text-muted text-center py-4">Cargando…</p>}
      {!loading && filtered.length === 0 && <p className="text-muted text-center py-4">Sin asignaciones.</p>}

      {filtered.length > 0 && (
        <div className="card shadow-sm">
          <div className="card-body p-0">
            <table className="table table-sm table-hover mb-0">
              <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ fontSize: '0.82em' }}>
                  <SortTh field="ip">IP</SortTh>
                  <th>Objeto</th>
                  <th>Modo</th>
                  <th>P</th>
                  <SortTh field="created_at">Fecha asig.</SortTh>
                  <SortTh field="assigned_by">Por</SortTh>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody style={{ fontSize: '0.83em' }}>
                {filtered.map(a => {
                  const atColor = ASSIGN_TYPE_COLORS[a.assignment_type] || 'secondary';
                  const name = assignmentName(a, ciCatalog);
                  const dateStr = a.created_at ? new Date(a.created_at).toLocaleDateString('es-ES', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—';
                  return (
                    <tr key={a.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 700 }}>{a.ip}</td>
                      <td style={{ maxWidth: 220 }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>
                          {name}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.82em', lineHeight: 1.2 }}>
                          {ciTypeLabel(a.object_type)}
                          {a.interface_ref ? ` · ${a.interface_ref}` : ''}
                        </div>
                      </td>
                      <td>
                        <span className={`badge text-bg-${atColor}`} style={{ fontSize: '0.72em' }}>
                          {(a.assignment_type || 'static').toUpperCase()}
                        </span>
                      </td>
                      <td>
                        {a.is_primary && <span className="badge text-bg-primary" style={{ fontSize: '0.68em' }}>P</span>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.78em' }} title={a.created_at}>{dateStr}</td>
                      <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.78em' }}
                          title={a.assigned_by || ''}>
                        {a.assigned_by || <span className="text-muted">—</span>}
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline-danger py-0 px-2" style={{ fontSize: '0.75em' }} onClick={() => release(a.id)}>
                          Liberar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </IPAMLayout>
  );
}
